import { Badge, Tab, Tabs } from "@mui/material";
import type { PageTabsConfig } from "./PageLayout.types";

const tabId = (prefix: string, value: string) => `${prefix}-tab-${value}`;
const panelId = (prefix: string, value: string) => `${prefix}-panel-${value}`;

/**
 * Spread on each of the page's tab panels: it wires the panel to its tab for assistive tech and
 * hides it when another tab is current. Panels stay mounted, so each keeps its state across
 * switches.
 */
export function pageTabPanelProps<T extends string>(tabs: PageTabsConfig<T>, value: T) {
	return {
		role: "tabpanel",
		id: panelId(tabs.id, value),
		"aria-labelledby": tabId(tabs.id, value),
		hidden: value !== tabs.value,
	} as const;
}

/** A counted tab lets its badge show past the label's top-right corner (Tab clips its overflow by default) and leaves
 *  room for it: `pr` for the badge to the right, `pt` for the half that rises above the text. */
const COUNTED_TAB_SX = { pt: 2, pb: 1, pr: 3.5, overflow: "visible" } as const;

/** MUI centres a badge on the label's corner, so half of a two-digit count covered the label's last letters
 *  (design-audit live pass: "VALIDATION", "REVIEW"). Pushing it one badge-width right clears the text; the
 *  tab's `pr` above is the room it needs. */
const COUNT_BADGE_SX = { "& .MuiBadge-badge": { right: -14 } } as const;

/** Page-level tabs. Scrollable, so any number of tabs fits a phone without wrapping. */
export function PageTabs<T extends string>(props: { tabs: PageTabsConfig<T> }) {
	const tabs = props.tabs;
	return (
		<Tabs
			value={tabs.value}
			onChange={(_, next: T) => tabs.onChange(next)}
			aria-label={tabs.label}
			variant="scrollable"
			scrollButtons="auto"
			allowScrollButtonsMobile
		>
			{tabs.items.map((item) => (
				<Tab
					key={item.value}
					value={item.value}
					disabled={tabs.disabled}
					label={
						item.count === undefined ? (
							item.label
						) : (
							<Badge badgeContent={item.count} color={tabs.countColor ?? "primary"} max={999} sx={COUNT_BADGE_SX}>
								{item.label}
							</Badge>
						)
					}
					aria-label={item.count === undefined ? undefined : `${item.label} (${item.count})`}
					sx={item.count === undefined ? undefined : COUNTED_TAB_SX}
					id={tabId(tabs.id, item.value)}
					aria-controls={panelId(tabs.id, item.value)}
				/>
			))}
		</Tabs>
	);
}
